import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Skeleton} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AnalyticsDetailsConversation} from '../../../../../models/api/analyticsMetricDetailsModel';
import {
  analyticsChangeConversationsPageAction,
  SIZE_CONVERSATIONS_PAGE,
} from '../../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsDetails,
  getAnalyticsDetailsConversationsPageNumber,
  getAnalyticsDetailsIsLoading,
} from '../../../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {helpCenterUrl} from '../../../../../store/stateless/navigationActions';
import {AnalyticsExportDialog} from '../../../exports/analyticsExportDialog';
import {AnalyticsTooltip} from '../../../reportElements/tooltips/analyticsTooltips';
import {CONVERSATION_CARD_HEIGHT} from '../../../reportViewer/reportCommons';
import {SidePanelSectionWrapper} from '../sidePanelSectionWrapper';
import {ConversationCard} from './conversationCard';
import {ConversationDeniedCard} from './conversationDeniedCard';
import {ConversationsSectionPagination} from './conversationsSectionPagination';

/*
 * Const.
 */

const NB_SKELETON_DEFAULT = 4;

/*
 * Styles.
 */

const StyledSectionTitle = styled.div`
  ${flexCenter}
`;

const StyledTitleDiv = styled.div`
  ${flexCenter}
  flex: 1;
`;

const StyledAnalyticsTooltip = styled(AnalyticsTooltip)`
  margin-left: 8px;
`;

const StyledSkeletonWrapper = styled.div`
  margin-bottom: 5px;
`;

const StyledIconButton = styled(IconButton)`
  margin-left: 12px;
`;

/*
 * Props.
 */

interface ConversationSectionProps {
  hasUpsell: boolean;
}

/*
 * Component.
 */

export const ConversationsSection: FC<ConversationSectionProps> = ({hasUpsell}) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const {
    conversations: {items, nonAccessibleConversationIds, metadata},
    selectedMetric,
  } = useAppSelector(getAnalyticsDetails);
  const {totalConversations, totalSegments, isFinal} = metadata;
  const isLoading = useAppSelector(getAnalyticsDetailsIsLoading);
  const conversationsPageNumber = useAppSelector(getAnalyticsDetailsConversationsPageNumber);
  const [showExportDialog, toggleShowExportDialog] = useToggle(false);

  const handleOnPageNumberChange = (pageNumber: number) =>
    dispatch(analyticsChangeConversationsPageAction(pageNumber));

  return (
    <SidePanelSectionWrapper
      sectionTitle={
        <StyledSectionTitle>
          <StyledTitleDiv>
            <Plural
              id="analytics-drawer-details-conversations-section-header"
              value={totalSegments}
              one="1 conversation"
              other="# conversations"
            />
            {maybeRenderMoreSegmentsThanConversationsInfo(totalConversations, totalSegments, selectedMetric)}
          </StyledTitleDiv>
          <StyledIconButton
            onClick={toggleShowExportDialog}
            label={<Trans id="metric-details-export-icon-label">Export metric details data</Trans>}
            iconName="export"
            color={theme.greys.shade60}
            isDisabled={hasUpsell}
          />
        </StyledSectionTitle>
      }
    >
      {showExportDialog && (
        <AnalyticsExportDialog
          onRequestClose={toggleShowExportDialog}
          title={<Trans id="analytics-export-metric-details-dialog-title">Export metric details data</Trans>}
          isMetricDetails
        />
      )}
      {renderConversations(items, isLoading, hasUpsell)}
      {maybeRenderNonAccessibleConversations(nonAccessibleConversationIds, isLoading, hasUpsell)}
      {maybeRenderPagination(
        totalConversations,
        isLoading,
        isFinal,
        conversationsPageNumber,
        handleOnPageNumberChange,
        hasUpsell,
      )}
    </SidePanelSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderConversations(
  items: ReadonlyArray<AnalyticsDetailsConversation>,
  isLoading: boolean,
  hasUpsell: boolean,
) {
  /**
   * If `items.length` equals 0 and isloading is true, it's either the first loading or there was no results in the previous one.
   * We display some skeletons.
   */
  if (items.length === 0 && isLoading) {
    return Array.from({length: NB_SKELETON_DEFAULT}).map((_, index) => renderSkeleton(index));
  }

  return items.map((conv) =>
    isLoading ? (
      renderSkeleton(conv.id) // We display as many skeletons as the number of previous cards.
    ) : (
      <ConversationCard conversation={conv} key={conv.id} isInteractive={!hasUpsell} />
    ),
  );
}

function maybeRenderNonAccessibleConversations(
  nonAccessibleIds: ReadonlyArray<number>,
  isLoading: boolean,
  hasUpsell: boolean,
) {
  if (isLoading || !nonAccessibleIds.length || hasUpsell) {
    return null;
  }

  return <ConversationDeniedCard nbNonAccessible={nonAccessibleIds.length} />;
}

function renderSkeleton(key: number) {
  return (
    <StyledSkeletonWrapper key={key}>
      <Skeleton borderRadius="8px" height={`${CONVERSATION_CARD_HEIGHT}px`} />
    </StyledSkeletonWrapper>
  );
}

function maybeRenderPagination(
  total: number,
  isLoading: boolean,
  isFinal: boolean,
  currentPage: number,
  onPageChange: (arg: number) => void,
  hasUpsell: boolean,
) {
  if (total <= SIZE_CONVERSATIONS_PAGE || hasUpsell) {
    return null;
  } // Don't show pager if there is only a single page or if upsell card is present.

  return (
    <ConversationsSectionPagination
      isFinal={isFinal}
      isLoading={isLoading}
      onPageChange={onPageChange}
      currentPage={currentPage}
    />
  );
}

function maybeRenderMoreSegmentsThanConversationsInfo(
  totalConversations: number,
  totalSegments: number,
  metricId: string | undefined,
) {
  if (totalSegments === totalConversations) {
    return null;
  }

  const tooltip = metricId
    ? {
        title: (
          <Trans id="analytics-side-panel-conversations-section-header-more-segments-title">
            Metric details
          </Trans>
        ),
        tooltipContent: (
          <Trans id="analytics-side-panel-conversations-section-header-more-segments">
            Some conversations in your data span multiple conversation segments, impacting this metric.
          </Trans>
        ),
        learnMoreUrl: `${helpCenterUrl}/t/x2hz4vd/conversation-segments`,
        metricId,
      }
    : undefined;

  return <StyledAnalyticsTooltip tooltip={tooltip} reportSectionId="metric_details_panel" />;
}
